import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { useCreatePickupOrder } from '@/api/pickup-orders/queries/usePickupOrders'
import { AppTooltip } from '@/components/AppTooltip'
import { Divider } from '@/components/Divider'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/useToast'

import { PkgWarehouse } from '../../../../api/user-package/types/UserPkg'
import InfoIcon from '../../../../components/icons/info.svg?react'
import { WarehousePreview } from '../../../../components/WarehousePreview'
import { UploadArea } from '../../ReceiptUpload/components/UploadArea'
import { WarehouseAddressPreview } from './WarehouseAddressPreview'

interface PrimarkFormProps {
  fullName: string
}

const PICKUP_STORE = 'XXXXXX XXXXXX XXX'

type PrimarkFormData = {
  pickupDate: string
  orderNumber: string
  barcodeFile: File | null
}

const defaultValues: PrimarkFormData = {
  pickupDate: '',
  orderNumber: '',
  barcodeFile: null,
}

export const PrimarkForm = ({ fullName }: PrimarkFormProps) => {
  const createMutation = useCreatePickupOrder()
  const { showSuccessToast, showErrorToast } = useToast()
  const [isAgreed, setIsAgreed] = useState(false)
  const [uploadAreaKey, setUploadAreaKey] = useState(0)
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PrimarkFormData>({
    defaultValues,
    mode: 'onSubmit',
  })

  const onSubmit = async (data: PrimarkFormData) => {
    if (!isAgreed) {
      showErrorToast('יש לאשר שהפרטים והברקוד נכונים')
      return
    }

    try {
      // Create the pickup order with file (upload happens on server)
      await createMutation.mutateAsync({
        pickupDate: data.pickupDate,
        orderNumber: data.orderNumber.trim(),
        barcodeFile: data.barcodeFile,
      })

      // Reset form on success
      reset(defaultValues)
      // Reset UploadArea by remounting it with a new key
      setUploadAreaKey(prev => prev + 1)
      showSuccessToast('החבילה נקלטה במערכת!', 'החבילה זמינה עכשיו בעמוד ׳החבילות שלי׳. לאחר האיסוף, הפרטים יתעדכנו ותוכלו להמשיך לשילוח.')
    } catch (_err) {
      // Error is handled by the mutation's error state
      showErrorToast('שגיאה ביצירת ההזמנה. נסה שוב.')
    }
  }

  return (
    <Card className='border border-[#d0d5dd] bg-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.08),0px_1px_2px_0px_rgba(0,0,0,0.05)]'>
      <CardContent className='p-6'>
        <div className=''>
          {/* Card Header */}
          <div className='mb-2 flex items-center gap-2'>
            <WarehousePreview warehouse={PkgWarehouse.UK} />
            <h2 className='text-text-primary text-right leading-6 font-medium'>הכתובת ה- Primark שלך - כתובת לאיסוף בלבד!</h2>
          </div>
          <p className='text-text-secondary mb-6 text-right text-sm leading-5'>
            העתק כל שורה בנפרד לפי השדות באתר הקניות שלך והזן את הפרטים המתאימים
          </p>
          <form onSubmit={handleSubmit(onSubmit)}>
            <WarehouseAddressPreview index={0} isAddressLine2={false} field={{ label: 'Full Name', value: fullName || 'Full Name' }} />
            <Divider className='bg-border-default mt-4 mb-3' />
            <WarehouseAddressPreview index={1} isAddressLine2={false} field={{ label: 'Pickup Store', value: PICKUP_STORE }} />
            <Divider className='bg-border-default mt-4 mb-3' />
            {/* Pickup Date Field */}
            <Input
              {...register('pickupDate', { required: 'יש להזין תאריך איסוף' })}
              labelProps={{ dir: 'ltr', className: 'mb-4' }}
              label='Pickup Date (התאריך שהזנתם לאיסוף ההזמנה)'
              id='pickup-date'
              type='date'
              className='h-12 w-full rounded-2xl border-[#e5e9f2]'
              placeholder='MM/DD/YYYY'
              aria-invalid={!!errors.pickupDate}
              helperText={errors.pickupDate?.message}
              error={!!errors.pickupDate}
            />
            <Divider className='bg-border-default mt-4 mb-3' />

            <Input
              {...register('orderNumber', { required: 'יש להזין מספר הזמנה' })}
              labelProps={{ dir: 'ltr', className: 'mb-4' }}
              label='Order Number- יש להזין כאן את מספר ההזמנה שקיבלתם'
              id='order-number'
              type='text'
              className='h-12 w-full rounded-2xl border-[#e5e9f2]'
              placeholder='GB1234567890'
              dir='ltr'
              aria-invalid={!!errors.orderNumber}
              helperText={errors.orderNumber?.message}
              error={!!errors.orderNumber}
            />
            <Divider className='bg-border-default mt-4 mb-3' />

            {/* Upload Barcode Field */}
            <div className='pb-4'>
              <Label dir='ltr' htmlFor='barcode-upload' className='text-text-primary mb-4 text-sm font-normal'>
                Upload Barcode (from your confirmation email or SMS) - עליך להעלות את הברקוד שקיבלת מהאימייל או הטלפון שלך
              </Label>
              <Controller
                name='barcodeFile'
                control={control}
                render={({ field }) => (
                  <UploadArea
                    maxFileCount={1}
                    key={uploadAreaKey}
                    onChangeFile={file => {
                      field.onChange(file)
                    }}
                  />
                )}
              />
              {errors.barcodeFile && <div className='text-destructive mt-2 text-right text-sm'>{errors.barcodeFile.message}</div>}
              <AppTooltip content='הברקוד הוא מסמך שמאשר שקיבלת את ההזמנה מהאימייל או הטלפון שלך'>
                <div className='text-text-link mt-2 flex items-center gap-2 text-xs underline'>
                  <InfoIcon className='size-4' />
                  מהו הברקוד שאני צריכ/ה להעלות?
                </div>
              </AppTooltip>
            </div>
            <Checkbox
              label='מאשר שהפרטים והברקוד נכונים'
              labelClassName='text-text-primary text-sm font-normal'
              checked={isAgreed}
              onCheckedChange={checked => setIsAgreed(checked === 'indeterminate' ? false : checked)}
            />

            {/* Submit Button */}
            <div className='flex flex-col items-center gap-2 pt-4'>
              <Button
                type='submit'
                loading={createMutation.isPending}
                disabled={createMutation.isPending || !isAgreed}
                className='h-12 w-[335px] rounded-full bg-[#fad600] font-medium text-[#333] hover:bg-[#fad600]/90'
              >
                יאללה, תאספו לי את הפריימרק
              </Button>
            </div>
          </form>
        </div>
      </CardContent>
    </Card>
  )
}
